import { Link } from "react-router-dom";
import { Building2, ArrowRight } from "lucide-react";
import BeratungDetailLayout, { BeratungSection } from "@/components/BeratungDetailLayout";

const BeratungOrganisationsentwicklung = () => (
  <BeratungDetailLayout
    icon={Building2}
    title="Organisationsentwicklung"
    intro="Wir begleiten Organisationen bei Veränderungsprozessen, Strukturentwicklung und kulturellem Wandel – systemisch, partizipativ und nachhaltig."
    seoTitle="Organisationsentwicklung – Leadership Focus"
    seoDescription="Systemische Organisationsentwicklung: Struktur, Rollen und Entscheidungswege klären, Reibung an Schnittstellen reduzieren und Veränderung wirksam verankern."
    canonical="https://leadership-focus.de/portfolio/beratung/organisationsentwicklung"
  >
    <BeratungSection heading="Wenn …">
      <p>
        Ihre Organisation steht unter Veränderungsdruck – Wachstum, Restrukturierung, neue Führung,
        neue Arbeitsformen – und trotzdem bleibt das Gefühl: „Wir kommen nicht sauber in die
        Umsetzung.“ Entscheidungen ziehen sich, Verantwortungen wirken unklar, Schnittstellen
        knirschen. HR/OE spürt: Es geht nicht um ein einzelnes Projekt, sondern um die Passung
        zwischen Struktur, Kultur und Alltag.
      </p>
    </BeratungSection>

    <BeratungSection heading="Typische Anlässe">
      <ul className="list-disc pl-5 space-y-1">
        <li>Reorganisation, M&amp;A-Integration, Skalierung</li>
        <li>Rollen-/Mandatsunklarheit zwischen Bereichen</li>
        <li>Kulturbruch: neue Erwartungen, alte Routinen</li>
        <li>Viele Initiativen, wenig Wirkung („Change-Müdigkeit“)</li>
      </ul>
    </BeratungSection>

    <BeratungSection heading="Dann …">
      <p>
        … begleiten wir Geschäftsführung und HR/OE dabei, die entscheidenden Stellhebel zu
        identifizieren: Wo verhindert die aktuelle Struktur gute Entscheidungen? Welche
        Kommunikationswege erzeugen Reibung? Welche kulturellen Selbstverständlichkeiten halten das
        Alte stabil? Wir arbeiten systemisch und partizipativ – nicht als „Workshop-Serie“, sondern
        als gezielte Architektur aus Klärung, Entscheidung und Stabilisierung.
      </p>
    </BeratungSection>

    <BeratungSection heading="Vorgehen">
      <ul className="list-disc pl-5 space-y-1">
        <li>
          <strong className="font-semibold text-foreground">Diagnose &amp; Fokus:</strong> Was ist
          beobachtbar? Wo hängt Wertschöpfung/Wirksamkeit?
        </li>
        <li>
          <strong className="font-semibold text-foreground">Design:</strong> Rollen, Mandate,
          Entscheidungswege und Routinen gezielt nachjustieren.
        </li>
        <li>
          <strong className="font-semibold text-foreground">Stabilisieren:</strong> In Führung,
          Formaten und Regeln verankern – ohne Überbürokratisierung.
        </li>
      </ul>
    </BeratungSection>

    <BeratungSection heading="Ergebnis …">
      <ul className="list-disc pl-5 space-y-1">
        <li>Klare Verantwortungen/Mandate und Entscheidungslogik</li>
        <li>Weniger Reibung an Schnittstellen, mehr Verlässlichkeit in der Zusammenarbeit</li>
        <li>Ein tragfähiger Umsetzungsrhythmus inkl. Reflexionsschleifen</li>
      </ul>
    </BeratungSection>

    <BeratungSection heading="Messsignale">
      <ul className="list-disc pl-5 space-y-1">
        <li>
          Anteil wiederkehrender Rollen-/Mandatsfragen in Meetings sinkt (z. B. in 10 Meetings
          zählen: „Wer entscheidet das?“).
        </li>
        <li>
          Schnittstellen-Reibung wird seltener eskaliert (weniger ad-hoc Eskalationen/„Feuerwehr“-
          Abstimmungen pro Woche).
        </li>
        <li>
          Entscheidungen werden stabiler (weniger Rücknahmen/Umdeutungen innerhalb von 4 Wochen nach
          Beschluss).
        </li>
      </ul>
    </BeratungSection>

    <BeratungSection heading="Risiko/Nebenwirkung (und Abfederung)">
      <p>
        Mehr Klarheit erzeugt kurzfristig mehr Spannung („Jetzt sieht man’s erst richtig“). Wir
        federn das ab, indem wir Konflikte nicht personalisieren, sondern als Struktur- und
        Entscheidungsfragen bearbeiten – und den Takt so wählen, dass das Tagesgeschäft nicht
        kollabiert.
      </p>
    </BeratungSection>

    <BeratungSection heading="Kostenlose Erstberatung">
      <p>
        Wir klären in einem ersten Gespräch, welche Stellhebel (Struktur, Entscheidungspunkte,
        Kommunikationsarchitektur) in Ihrem Kontext den größten Unterschied machen.
      </p>
      <Link
        to="/kontakt"
        className="mt-2 inline-flex items-center gap-2 rounded-md bg-primary px-5 py-3 text-sm font-medium text-primary-foreground transition-opacity hover:opacity-90"
      >
        Erstgespräch vereinbaren
        <ArrowRight className="h-4 w-4" />
      </Link>
    </BeratungSection>
  </BeratungDetailLayout>
);

export default BeratungOrganisationsentwicklung;
